import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import type { WheelItemsData, WheelItem } from '../wheelData'

import DetailedDescription from './DetailedDescription'
import getParents from './getParents'
import ShortDescription from './ShortDescription'

type Props = {
  activeItems: Set<WheelItem>
  wheelItemsData: WheelItemsData
}

export default function WheelDescription({ activeItems, wheelItemsData }: Props) {
  const [areRelatives, child] = useMemo(
    () =>
      selectedElementsAreRelatives({
        activeItems,
        wheelItemsData,
      }),
    [activeItems, wheelItemsData],
  )

  if (activeItems.size === 0) {
    return <Placeholder />
  } else if (areRelatives) {
    return <DetailedDescription item={child} wheelItemsData={wheelItemsData} />
  } else {
    return <ShortDescription activeItems={activeItems} wheelItemsData={wheelItemsData} />
  }
}

function selectedElementsAreRelatives({
  activeItems,
  wheelItemsData,
}: {
  activeItems: Set<WheelItem>
  wheelItemsData: WheelItemsData
}): [false, null] | [true, WheelItem] {
  if (activeItems.size === 0) return [false, null] as const

  const areRelatives = activeItems
    .values()
    .toArray()
    .map((item) => {
      const parents = getParents(item, wheelItemsData)
      return [parents, item] as const
    })
    .every(([parents, item], _index, array) => {
      return array.every(([otherParents, otherItem]) => {
        if (item === otherItem) return true
        if (parents.includes(otherItem)) return true
        if (otherParents.includes(item)) return true
        return false
      })
    })

  if (areRelatives) {
    const child = activeItems
      .values()
      .toArray()
      .reduce((a, b) => {
        const aParents = getParents(a, wheelItemsData)
        const bParents = getParents(b, wheelItemsData)
        if (aParents.includes(b)) {
          return a
        } else if (bParents.includes(a)) {
          return b
        } else {
          return a
        }
      })
    return [true, child] as const
  }

  return [false, null] as const
}

function Placeholder() {
  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="card card-sm bg-base-300 shadow-sm">
        <div className="card-body">
          <div className="card-title text-xl">
            <FormattedMessage id="placeholder.title" defaultMessage="Ready to start?" />
          </div>
          <div className="prose max-w-none">
            <FormattedMessage
              id="placeholder.subtitle"
              defaultMessage="<b>Click</b>, <b>turn</b>, and <b>explore</b> your emotions with <b>Emotion Wheel</b>"
              values={{
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
